// Registro del Service Worker que sirve los assets boot-críticos desde un bundle
// único (ver public/sw.js + scripts/buildAssetBundle.mjs). Mantiene main.ts limpio.
//
// - PROD: registra /sw.js (pasándole la base de assets de config.json por query) y
//   resuelve `assetsReady` recién cuando el SW está ACTIVO y CONTROLANDO la página
//   (lo que implica que el install desempaquetó el bundle a Cache Storage). El boot
//   espera esa promise antes de `new Phaser.Game`, así el preload de Phaser ya pega
//   contra el cache local en vez de bajar cientos de archivos por red.
// - DEV: NO registra SW (Vite sirve /assets/* file-por-file, mejor iteración) y
//   desregistra cualquier SW viejo para no envenenar el hot-reload. Resuelve al toque.

let resolveReady: () => void = () => {};

/** Se resuelve cuando los assets están listos para que arranque Phaser. */
export const assetsReady: Promise<void> = new Promise<void>((res) => {
  resolveReady = res;
});

/** Espera (con timeout de cortesía) a que el SW pase a controlar esta página. */
async function waitForController(timeoutMs: number): Promise<void> {
  if (navigator.serviceWorker.controller) return;
  await new Promise<void>((res) => {
    const done = (): void => {
      navigator.serviceWorker.removeEventListener('controllerchange', done);
      clearTimeout(timer);
      res();
    };
    const timer = setTimeout(done, timeoutMs);
    navigator.serviceWorker.addEventListener('controllerchange', done);
  });
}

/**
 * Arranca el registro del SW. NO bloquea: se llama al inicio del boot para que el
 * bundle se baje durante el login. El gate real es `await assetsReady`.
 *
 * @param assetsBaseUrl Base de assets resuelta de config.json (la misma de
 *   setAssetsBaseUrl). El SW intercepta requests bajo esta base — clave para el
 *   futuro bucket: cambiar config.json basta, sin tocar el SW.
 */
export function registerAssetBundleSW(assetsBaseUrl: string): void {
  if (!('serviceWorker' in navigator)) {
    resolveReady();
    return;
  }

  if (import.meta.env.DEV) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
    resolveReady();
    return;
  }

  void (async () => {
    try {
      const swUrl = `/sw.js?base=${encodeURIComponent(assetsBaseUrl)}`;
      await navigator.serviceWorker.register(swUrl);
      // `ready` resuelve cuando hay un SW activo; como el install bloquea la
      // activación hasta desempaquetar el bundle (waitUntil), esto ya espera la
      // descarga completa.
      await navigator.serviceWorker.ready;
      // En la 1ra visita el SW activa con clients.claim pero el controller se setea
      // un instante después. Sin controller, el preload no sería interceptado.
      await waitForController(8000);
    } catch (err) {
      // Si algo falla, se cae a carga directa por red (el juego igual funciona).
      console.warn('[assetBundle] SW no disponible, carga directa:', err);
    } finally {
      resolveReady();
    }
  })();
}
